import React, { memo, useMemo } from 'react';
import { prop } from 'ramda';
import { useAppSelector } from 'js/store/hooks';
import { ReCaptcha } from 'js/components/main/ReCaptcha';
import { Input } from 'js/components/common/Fields/Input';
import { Label } from 'js/components/common/Fields/Label';
import { Select } from 'js/components/common/Fields/Select';
import { useComponentTranslations } from 'js/hooks/component';
import { FORM_FIELD_TYPE } from 'js/components/common/Form/constants';

import { AREA_CODES } from '../../constants';
import { getPhoneAreaCodeList } from '../../helpers';
import { useFieldLabel } from '../ChangeFormField/hooks';

import * as Types from './types';

import style from './PhoneField.scss';

export const PhoneFieldBare: React.FC<Types.IPhoneFieldConfig> = (props) => {
    const {
        field,
        fieldId,
        validate,
        disabled,
        className,
        classNames,
        isFieldHidden,
        isReCaptchaVisible,
        linkedPasswordFields,
        onReCaptchaTokenError,
        onReCaptchaTokenValidate
    } = props;
    const {
        name,
        type,
        readOnly,
        areaCode,
        baseType,
        placeholder: fieldPlaceholder,
        infoTooltip,
        validationRules
    } = field;
    const {
        isLabelAsteriskEnabled,
        isPlaceholderAsteriskEnabled
    } = validationRules?.find(rule => rule.type === 'required') || {};
    const translate = useComponentTranslations();
    const allowedCountries = useAppSelector(prop('allowedCountries'));
    const phoneAreaCodeList = useMemo(() => getPhoneAreaCodeList(allowedCountries), [allowedCountries]);
    const [label, getLabelText] = useFieldLabel(field.label);
    const placeholder = fieldPlaceholder && translate(fieldPlaceholder, fieldPlaceholder);

    return (
        <div className={`${className} ${style.phoneContainer}`}>
            {label && (
                <Label
                    id={fieldId}
                    label={label}
                    {...readOnly && { readOnly: true, classNames: { label: style.disabledLabel } }}
                    isLabelAsteriskEnabled={isLabelAsteriskEnabled}
                    infoTooltip={infoTooltip && translate(infoTooltip, infoTooltip)}
                />
            )}
            <div className={style.phone}>
                {
                    areaCode !== AREA_CODES.HIDDEN && (
                        <Select
                            name={`${name}-areaCode`}
                            id={`${fieldId}-areaCode`}
                            options={phoneAreaCodeList}
                            className={style.areaCode}
                            disabled={disabled || areaCode === AREA_CODES.PREDEFINED || readOnly}
                            aria-label={getLabelText() || placeholder || translate('playtech.mobile.system.select.aria-label')}
                            classNames={{
                                validationIcon: style.validationIcon,
                                content: `${style.content} ${disabled || areaCode === AREA_CODES.PREDEFINED || readOnly ? style.disabled : ''}`
                            }}
                        />
                    )
                }
                {
                    areaCode === AREA_CODES.HIDDEN && (
                        <input
                            type="hidden"
                            name={`${name}-areaCode`}
                        />
                    )
                }
                <Input
                    name={name}
                    id={fieldId}
                    type={baseType}
                    disabled={disabled}
                    placeholder={placeholder}
                    {...readOnly && { readOnly: true }}
                    isPlaceholderAsteriskEnabled={isPlaceholderAsteriskEnabled}
                    aria-label={getLabelText() || placeholder || translate('playtech.mobile.system.input.aria-label')}
                    className={`${style.phoneInput} ${areaCode === AREA_CODES.HIDDEN ? style.noAreaCodePhoneInput : ''}`}
                    {...validate && {
                        validate,
                        validateFields: linkedPasswordFields,
                        // to rerender field with a new validateFields, it's needed to update another prop based on new validateFields
                        data: `${fieldId}_${linkedPasswordFields.join('-')}`
                    }}
                />
            </div>
            {type === FORM_FIELD_TYPE.PHONE && isReCaptchaVisible && !isFieldHidden && (
                <div className={classNames.captcha}>
                    <ReCaptcha
                        onError={onReCaptchaTokenError}
                        operation="checkUsernameAvailability"
                        onValidate={onReCaptchaTokenValidate}
                    />
                </div>
            )}
        </div>
    );
};

export const PhoneField = memo(PhoneFieldBare);
